import React from 'react';
import {Box, Link as JoyLink, Typography} from "@mui/joy";
import {SxProps} from "@mui/joy/styles/types";
import {CallRounded, MailRounded} from "@mui-symbols-material/w300";

interface Props {
    email?: string | null;
    phone?: string | null;
    // 'body-sm' (default): 14px in Joy's text.tertiary grey — the contact-card
    // look. 'bodySmall': 12px in brand primary — the compact hero/widget look.
    // The colour follows the level: Joy's stock level carries its own text
    // colour, the brand level does not, so the link's primary shows through.
    level?: 'body-sm' | 'bodySmall';
    sx?: SxProps;
    'data-testid'?: string;
}

/**
 * Email and phone of a person or hotline as icon-decorated mailto:/tel: links,
 * one per line — the single renderer for these rows on the node contact tab
 * and the service-unit surfaces, so contact channels look the same wherever
 * they appear. The wrapper is always rendered (even without any channel) so a
 * caller can reserve a fixed height and keep sibling cards aligned.
 */
const ContactChannelLinks = ({email, phone, level = 'body-sm', sx, 'data-testid': testId}: Props) => {
    const renderLink = (href: string, text: string, icon: React.ReactNode) => (
        <JoyLink
            href={href}
            level={level}
            startDecorator={icon}
            sx={{width: 'fit-content', maxWidth: '100%'}}
        >
            <Typography noWrap level={level}>{text}</Typography>
        </JoyLink>
    );

    return (
        <Box
            data-testid={testId}
            sx={[
                {display: 'flex', flexDirection: 'column', gap: '2px'},
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {email && renderLink(`mailto:${email}`, email, <MailRounded sx={{fontSize: 14}}/>)}
            {phone && renderLink(`tel:${phone}`, phone, <CallRounded sx={{fontSize: 14}}/>)}
        </Box>
    );
};

export default ContactChannelLinks;
